import { useEffect, useState } from 'react'
import type { LimitSetting, WorkerLimits } from '@shared/worker'
import { workersApi } from '../../lib/api'
import { cn } from '../../lib/utils'

const ROWS: { key: Exclude<keyof WorkerLimits, 'enabled' | 'noStopCap'>; label: string; hint: string; max: number }[] = [
  { key: 'maxWorkerRiskPct', label: 'One worker’s max loss', hint: 'of net liquidation, if every stop fills', max: 100 },
  { key: 'maxOpenRiskPct', label: 'All running workers’ risk', hint: 'of net liquidation, at their current stops', max: 100 },
  { key: 'maxSymbolExposurePct', label: 'Exposure to one symbol', hint: 'of net liquidation (IBKR position + workers)', max: 500 },
  { key: 'maxDailyLossPct', label: 'Stop new workers after a daily loss of', hint: 'of net liquidation', max: 100 },
]

export function Switch({ on, onChange, label }: { on: boolean; onChange: (v: boolean) => void; label: string }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      onClick={() => onChange(!on)}
      className={cn('relative h-5 w-9 shrink-0 rounded-full transition-colors', on ? 'bg-[var(--color-accent)]' : 'bg-[var(--color-surface-3)]')}
    >
      <span className={cn('absolute top-0.5 h-4 w-4 rounded-full bg-[var(--color-base)] transition-transform', on ? 'translate-x-[18px]' : 'translate-x-0.5')} />
    </button>
  )
}

/** Global risk limits for trade workers — off by default; the user decides how much risk to take. */
export function TradingRiskSettings() {
  const [l, setL] = useState<WorkerLimits | null>(null)
  const [saved, setSaved] = useState<WorkerLimits | null>(null)
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  useEffect(() => {
    workersApi.limits().then(
      (x) => {
        setL(x)
        setSaved(x)
      },
      (e) => setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Could not load the limits' }),
    )
  }, [])

  const dirty = !!l && !!saved && JSON.stringify(l) !== JSON.stringify(saved)
  const save = async (next: WorkerLimits) => {
    try {
      const r = await workersApi.setLimits(next)
      setL(r)
      setSaved(r)
      setMsg({ tone: 'ok', text: r.enabled ? 'Saved — active limits now block approvals that break them.' : 'Saved — limits are off; the card shows them for information only.' })
    } catch (e) {
      setMsg({ tone: 'err', text: e instanceof Error ? e.message : 'Save failed' })
    }
  }
  const setRow = (key: (typeof ROWS)[number]['key'], patch: Partial<LimitSetting>) => l && setL({ ...l, [key]: { ...l[key], ...patch } })

  return (
    <section id="settings-trading" className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-fg">Trading risk limits</h2>
          <p className="mt-0.5 text-[12.5px] text-fg-subtle">
            Off by default. While off, every approval card still shows these numbers — they just never block you.
          </p>
        </div>
        {l && <Switch on={l.enabled} label="Enable risk limits" onChange={(v) => void save({ ...l, enabled: v })} />}
      </div>

      {l && (
        <div className={cn('mt-3 divide-y divide-[var(--separator)] rounded-lg border border-[var(--line-soft)]', !l.enabled && 'opacity-60')}>
          {ROWS.map((r) => (
            <div key={r.key} className="flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]">
              <span className="min-w-0">
                <span className="text-fg-muted">{r.label}</span>
                <span className="block text-[11px] text-fg-faint">{r.hint}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <input
                  type="number"
                  step="0.5"
                  min="0.1"
                  max={r.max}
                  value={l[r.key].pct}
                  disabled={!l[r.key].on}
                  onChange={(e) => setRow(r.key, { pct: Number(e.target.value) })}
                  className="num w-16 rounded-md border border-[var(--line-soft)] bg-[var(--color-inset)] px-2 py-1 text-right text-fg focus:outline-none disabled:opacity-40"
                />
                <span className="w-3 text-[11px] text-fg-faint">%</span>
                <Switch on={l[r.key].on} label={`Enable: ${r.label}`} onChange={(v) => setRow(r.key, { on: v })} />
              </span>
            </div>
          ))}
        </div>
      )}

      {l && (
        <div className="mt-3 rounded-lg border border-[var(--line-soft)] px-3 py-2.5 text-[12.5px]">
          <div className="text-fg-muted">
            Workers without a stop
            <span className="block text-[11px] text-fg-faint">
              Always on, even with the limits off: a no-stop worker can lose its whole cost, so this caps what it may spend.
            </span>
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2">
            {(
              [
                ['perTradeUsd', 'One trade'],
                ['totalUsd', 'All no-stop workers'],
              ] as const
            ).map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-fg-subtle">
                {label}
                <span className="text-[11px] text-fg-faint">$</span>
                <input
                  type="number"
                  step="50"
                  min="1"
                  value={(l.noStopCap ?? { perTradeUsd: 750, totalUsd: 2000 })[k]}
                  onChange={(e) => setL({ ...l, noStopCap: { ...(l.noStopCap ?? { perTradeUsd: 750, totalUsd: 2000 }), [k]: Number(e.target.value) } })}
                  className="num w-24 rounded-md border border-[var(--line-soft)] bg-[var(--color-inset)] px-2 py-1 text-right text-fg focus:outline-none"
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {l && dirty && (
        <div className="mt-3 flex items-center gap-2">
          <button onClick={() => void save(l)} className="rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--color-base)]">
            Save limits
          </button>
          <button onClick={() => setL(saved)} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
            Discard
          </button>
        </div>
      )}
      {msg && <div className={cn('mt-2 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
    </section>
  )
}
